import { PageId } from 'constants/pages'
import { isEmpty } from 'lodash'
import {
  NOT_UPSELL_LOCALIZATIONS,
  PaymentSystem,
} from 'nove8/constants/payment'
import { TStep } from 'nove8/types'
import { LOCALES } from 'root-constants'
import { StatesConfig } from 'xstate'
import { useZustandGlobalLocalStore } from 'zustandGlobalLocalStore'
import { useZustandSessionStore } from 'zustandGlobalSessionStore'
import { useZustandGlobalStore } from 'zustandGlobalStore'
import { getHasBasicSubscription } from '../../helpers/getHasBasicSubscription'
import { PageIdWoofzSmdBl } from './constants/pages'

const getHasEmail = () => !!useZustandGlobalStore.getState().lead?.email
const getHasSubscription = () =>
  getHasBasicSubscription(
    useZustandGlobalStore.getState().lead?.subscriptions || null,
  )
const getHasAccount = () =>
  !!useZustandGlobalStore.getState().lead?.firebaseToken
const getHasAnswers = () =>
  !isEmpty(
    useZustandSessionStore.getState().userAnswers ||
      useZustandGlobalStore.getState().lead?.quizzes ||
      null,
  )

const isPaypalPayment = () =>
  ((useZustandGlobalStore.getState().lead?.subscriptions?.[0]
    ?.paymentSystemType as unknown) as PaymentSystem) === PaymentSystem.PAYPAL

const isNoAgressiveBehavior = () => {
  const agressiveBehaviorAnswer =
    useZustandSessionStore.getState().userAnswers?.agressive_behavior_smd_bl ||
    useZustandGlobalStore.getState().lead?.quizzes?.agressive_behavior_smd_bl
  return agressiveBehaviorAnswer === 'no'
}

const isDogActSame = () => {
  const dogActAnswer =
    useZustandSessionStore.getState().userAnswers?.dog_act_smd_bl ||
    useZustandGlobalStore.getState().lead?.quizzes?.dog_act_smd_bl
  return dogActAnswer === 'no'
}

export const getXStateConfigWoofzSmdBl = (
  pages: TStep[],
): StatesConfig<any, any, any> => ({
  initial: {
    always: [
      {
        cond: (): boolean => !!useZustandGlobalLocalStore.getState().purchaseId,
        target:
          useZustandGlobalLocalStore.getState().purchasePageId ||
          pages[0].pageId,
      },
      {
        cond: (): boolean => {
          const hasEmail = getHasEmail()
          const url = new URL(window.location.href)
          const pageId = url.pathname.split('/').pop()
          const isNewsletterPage = pageId === PageId.NEWSLETTER_URGE_SMD_BL

          return !hasEmail && isNewsletterPage
        },
        target: pages.find((p) => p.pageId.includes('email'))?.pageId,
      },
      {
        cond: (): boolean => {
          const hasEmail = getHasEmail()
          const hasSubscription = getHasSubscription()
          const hasAccount = getHasAccount()
          const hasAnswers = getHasAnswers()
          const pageIdFromUrl = window.location.pathname.split('/').pop() || ''
          return (
            !hasEmail &&
            !hasSubscription &&
            !hasAccount &&
            hasAnswers &&
            pageIdFromUrl !== PageIdWoofzSmdBl.SCRATCH_SCREEN_SMD_BL
          )
        },
        target:
          pages.find((dynamicPage) => {
            const pageIdFromUrl =
              window.location.pathname.split('/').pop() || ''
            return pageIdFromUrl === dynamicPage.pageId
          })?.pageId || pages[0].pageId,
      },
      {
        cond: (): boolean => {
          const pageIdFromUrl = window.location.pathname.split('/').pop() || ''
          return pageIdFromUrl === PageIdWoofzSmdBl.SCRATCH_SCREEN_SMD_BL
        },
        target: PageIdWoofzSmdBl.EMAIL_SMD_BL,
      },
      {
        cond: (): boolean => getHasAccount(),
        target: PageIdWoofzSmdBl.DOWNLOAD_SMD_BL,
      },
      {
        cond: (): boolean => getHasSubscription(),
        target: PageIdWoofzSmdBl.ACCOUNT_SMD_BL,
      },
      {
        cond: (): boolean => getHasEmail(),
        target: PageIdWoofzSmdBl.SUBSCRIPTIONS_SMD_BL,
      },
      {
        target: pages[0].pageId,
      },
    ],
  },
  ...pages.reduce(
    (acc, page, index) => ({
      ...acc,
      [page.pageId]: {
        on: {
          NEXT_FLOW_STEP: {
            target: pages[index + 1]?.pageId,
          },
          SKIP_ONE_STEP: {
            target: pages[index + 2]?.pageId,
          },
          PREVIOUS_FLOW_STEP: {
            target:
              index - 1 >= 0 ? pages[index - 1].pageId : pages[index].pageId,
          },
          SKIP_ONE_STEP_BACK: {
            target:
              index - 2 >= 0 ? pages[index - 2].pageId : pages[index].pageId,
          },
          ...(page.pageId === PageId.AGRESSIVE_BEHAVIOR_SMD_BL && {
            NEXT_FLOW_STEP: [
              {
                cond: isNoAgressiveBehavior,
                target: PageId.BEHAVIOR_ISSUES_SMD_BL,
              },
              {
                target: PageId.TRIGGERS_SMD_BL,
              },
            ],
          }),
          ...(page.pageId === PageId.BEHAVIOR_ISSUES_SMD_BL && {
            PREVIOUS_FLOW_STEP: [
              {
                cond: isNoAgressiveBehavior,
                target: PageId.AGRESSIVE_BEHAVIOR_SMD_BL,
              },
              {
                target: PageId.URGE_2_SMD_BL,
              },
            ],
          }),
          ...(page.pageId === PageId.DOG_ACT_SMD_BL && {
            NEXT_FLOW_STEP: [
              {
                cond: isDogActSame,
                target: PageId.PREPARE_SCREEN_SMD_BL,
              },
              {
                target: PageId.DOG_OFF_AT_HOME_SMD_BL,
              },
            ],
          }),
          ...(page.pageId === PageId.DOG_PROFILE_SMD_BL && {
            PREVIOUS_FLOW_STEP: [
              {
                cond: isDogActSame,
                target: PageId.DOG_ACT_SMD_BL,
              },
              {
                target: PageId.DOG_OFF_AT_HOME_SMD_BL,
              },
            ],
          }),
          ...(page.pageId === PageId.SUBSCRIPTIONS_SMD_BL && {
            NEXT_FLOW_STEP: [
              {
                cond: isPaypalPayment,
                target: PageIdWoofzSmdBl.ACCOUNT_SMD_BL,
              },
              {
                target: PageId.CHAT_UPSELL_SMD_BL,
              },
            ],
          }),
          ...(page.pageId === PageId.CHAT_UPSELL_SMD_BL && {
            NEXT_FLOW_STEP: [
              {
                cond: () => {
                  const url = new URL(window.location.href)
                  const languageFromUrl = url.searchParams.get('lang')
                  return NOT_UPSELL_LOCALIZATIONS.includes(
                    languageFromUrl as string,
                  )
                },
                target: PageIdWoofzSmdBl.ACCOUNT_SMD_BL,
              },
              {
                target: PageId.UPSELL_SMD_BL,
              },
            ],
          }),
          ...(page.pageId === PageId.UPSELL_SMD_BL && {
            NEXT_FLOW_STEP: [
              {
                cond: () => {
                  const url = new URL(window.location.href)
                  const languageFromUrl = url.searchParams.get('lang')
                  return languageFromUrl === LOCALES.EN
                },
                target: PageId.UPSELL_FEEDING_GUIDE_SMD_BL,
              },
              {
                target: PageIdWoofzSmdBl.ACCOUNT_SMD_BL,
              },
            ],
          }),
          ...(page.pageId === PageId.UPSELL_FEEDING_GUIDE_SMD_BL && {
            NEXT_FLOW_STEP: PageId.UPSELL_EMOTIONAL_SUPPORT_SMD_BL,
          }),
          ...(page.pageId === PageId.UPSELL_EMOTIONAL_SUPPORT_SMD_BL && {
            NEXT_FLOW_STEP: PageIdWoofzSmdBl.ACCOUNT_SMD_BL,
          }),
        },
      },
    }),
    {},
  ),
  [PageIdWoofzSmdBl.ACCOUNT_SMD_BL]: {
    on: {
      NEXT_FLOW_STEP: PageId.DOWNLOAD_SMD_BL,
    },
  },
  [PageId.DOWNLOAD_SMD_BL]: {
    type: 'final',
  },
})
